import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { ArrowLeft, Check, X, ShieldAlert } from "lucide-react";

export default function StudentEditPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [student, setStudent] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, cs] = await Promise.all([api.getStudent(id), api.listCoaches()]);
        setStudent(s);
        setForm({
          name: s.name,
          age: s.age,
          phone_number: s.phone_number,
          start_date: s.start_date,
          end_date: s.end_date,
          amount_received: s.amount_received || 0,
          pending_fee: s.pending_fee || 0,
          next_due_date: s.next_due_date || "",
          received_date: s.received_date || "",
          third_installment_amount: s.third_installment_amount || 0,
          third_installment_date: s.third_installment_date || "",
          comments: s.comments || "",
          coach_id: s.coach_id || "",
        });
        setCoaches(cs);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading || !form) return <div className="text-center py-16 text-slate-500">Loading...</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        amount_received: Number(form.amount_received),
        pending_fee: Number(form.pending_fee),
        next_due_date: form.next_due_date || null,
        third_installment_amount: Number(form.third_installment_amount) || 0,
        third_installment_date: form.third_installment_date || null,
        comments: (form.comments || "").slice(0, 800) || null,
      };
      const updated = await api.updateStudent(id, payload);
      toast.success("Student updated");
      setStudent(updated);
      if (updated.approval_status !== "pending") {
        nav(`/students/${id}`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setReviewing(true);
    try {
      // Save any edits first, then approve
      const payload = {
        ...form,
        age: Number(form.age),
        amount_received: Number(form.amount_received),
        pending_fee: Number(form.pending_fee),
        next_due_date: form.next_due_date || null,
        received_date: form.received_date || null,
        third_installment_amount: Number(form.third_installment_amount) || 0,
        third_installment_date: form.third_installment_date || null,
        comments: (form.comments || "").slice(0, 800) || null,
      };
      await api.updateStudent(id, payload);
      await api.approveStudent(id);
      toast.success("Student approved");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReviewing(false);
    }
  };

  const reject = async () => {
    if (!window.confirm("Reject and discard this student submission?")) return;
    setReviewing(true);
    try {
      await api.rejectStudent(id);
      toast.success("Submission rejected");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReviewing(false);
    }
  };

  const isPending = student?.approval_status === "pending";

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Link to={isPending ? "/dashboard" : `/students/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" />
        Back
      </Link>
      <h1 className="text-3xl font-extrabold text-slate-900">
        {isPending ? "Review & Approve" : "Edit Student"}
      </h1>

      {isPending && (
        <div className="flex items-start gap-3 bg-[#FF950014] border border-[#FF950055] rounded-xl p-4">
          <ShieldAlert className="w-5 h-5 text-[#FF9500] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 text-sm">
              Pending submission from {student?.coach_name || "coach"}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              Review the details below. You can edit before approving. Approving will make the student visible in the list; rejecting will discard the submission.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4 bg-slate-50 border border-slate-100 rounded-xl p-6">
        <F label="Name">
          <input data-testid={TID.studentNameInput} className="input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Age">
            <input data-testid={TID.studentAgeInput} type="number" className="input" value={form.age} onChange={(e) => set("age", e.target.value)} />
          </F>
          <F label="Phone">
            <input data-testid={TID.studentPhoneInput} className="input" value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} />
          </F>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <F label="Start"><input data-testid={TID.studentStartInput} type="date" className="input" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></F>
          <F label="End"><input data-testid={TID.studentEndInput} type="date" className="input" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} /></F>
        </div>
        {/* Installment 1 */}
        <div className="grid grid-cols-2 gap-4">
          <F label="Amount Received (₹)"><input data-testid={TID.studentAmountInput} type="number" className="input" value={form.amount_received} onChange={(e) => set("amount_received", e.target.value)} /></F>
          <F label="Received Date"><input data-testid="student-received-date" type="date" className="input" value={form.received_date} onChange={(e) => set("received_date", e.target.value)} /></F>
        </div>
        {/* Installment 2 */}
        <div className="grid grid-cols-2 gap-4">
          <F label="Pending ₹ (if app)"><input data-testid={TID.studentPendingInput} type="number" className="input" value={form.pending_fee} onChange={(e) => set("pending_fee", e.target.value)} /></F>
          <F label="Due Date (if app)"><input data-testid={TID.studentDueInput} type="date" className="input" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} /></F>
        </div>
        {/* Installment 3 */}
        <div className="grid grid-cols-2 gap-4">
          <F label="Part-3 Amnt ₹"><input data-testid="student-third-installment-amount" type="number" min={0} step="0.01" className="input" value={form.third_installment_amount} onChange={(e) => set("third_installment_amount", e.target.value)} /></F>
          <F label="Part-3 Date (if app)"><input data-testid="student-third-installment-date" type="date" className="input" value={form.third_installment_date} onChange={(e) => set("third_installment_date", e.target.value)} /></F>
        </div>
        <F label="Comments (Optional)">
          <textarea data-testid="student-comments" rows={3} maxLength={800} className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-sm outline-none focus:border-[#0A2540]" value={form.comments} onChange={(e) => set("comments", e.target.value)} placeholder="Any notes about this student (max ~100 words)" />
        </F>
        <F label="Coach">
          <select data-testid={TID.studentCoachSelect} className="input" value={form.coach_id} onChange={(e) => set("coach_id", e.target.value)}>
            <option value="">Select coach</option>
            {coaches.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
        </F>
        <button type="submit" data-testid={TID.studentSaveBtn} disabled={saving || reviewing} className="w-full h-12 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-60 text-white rounded-lg font-semibold">
          {saving ? "Saving..." : isPending ? "Save Edits" : "Save Changes"}
        </button>

        {isPending && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              data-testid={TID.reviewRejectBtn}
              onClick={reject}
              disabled={saving || reviewing}
              className="h-12 rounded-lg border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B3010] disabled:opacity-60 font-semibold flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Reject
            </button>
            <button
              type="button"
              data-testid={TID.reviewApproveBtn}
              onClick={approve}
              disabled={saving || reviewing}
              className="h-12 rounded-lg bg-[#34C759] hover:bg-[#2AA84A] disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              {reviewing ? "Approving..." : "Approve"}
            </button>
          </div>
        )}
      </form>
      <style>{`
        .input{width:100%;height:44px;padding:0 12px;border:1px solid #E5E7EB;border-radius:8px;background:#fff;color:#0F172A;font-size:14px;outline:none;}
        .input:focus{border-color:#0A2540;box-shadow:0 0 0 3px rgba(10,37,64,0.1);}
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
